import * as React from "react";

export type PriceInputProps = {
	value: number | undefined;
	onChange: (value: string) => void;
	placeholder?: string;
	disabled?: boolean;
	label?: string;
	error?: string;
	formatThousands?: boolean;
	className?: string;
	required?: boolean;
	iconOrText?: React.ReactNode | string;
	iconOrTextPosition?: "left" | "right";
	decimals?: number;
} & Omit<
	React.ComponentProps<"input">,
	"type" | "onChange" | "value" | "placeholder"
>;

function cn(...inputs: (string | undefined | null | boolean)[]) {
	return inputs.filter(Boolean).join(" ");
}

function normalizeDecimalSeparator(value: string): string {
	return value.replace(".", ",");
}

function parseFrenchNumber(value: string): number {
	if (value === "" || value === null || value === undefined) return Number.NaN;
	const cleaned = value.replace(/\s/g, "");
	const normalized = cleaned.replace(",", ".");
	return Number.parseFloat(normalized);
}

function formatWithThousandsSeparators(
	value: string,
	decimalSeparator = ",",
): string {
	if (!value) return "";

	const hasDecimalSeparator = value.includes(decimalSeparator);
	const parts = value.split(decimalSeparator);
	const integerPart = parts[0] ?? "";
	const decimalPart = parts[1] ?? "";

	const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

	if (hasDecimalSeparator) {
		return `${formattedInteger}${decimalSeparator}${decimalPart}`;
	}
	return formattedInteger;
}

function removeLeadingZeros(value: string): string {
	const parts = value.split(",");
	const integerPart = parts[0] ?? "";
	const decimalPart = parts[1];

	const cleanedInteger = integerPart.replace(/^0+/g, "") || "0";

	if (decimalPart !== undefined) {
		return `${cleanedInteger},${decimalPart}`;
	}
	return cleanedInteger;
}

function limitToExactDecimals(value: string, decimals: number): string {
	const parts = value.split(",");
	const integerPart = parts[0] ?? "";
	const decimalPart = parts[1];

	if (decimalPart === undefined) {
		return integerPart;
	}

	const limitedDecimals = decimalPart.slice(0, decimals);
	return `${integerPart},${limitedDecimals}`;
}

function ensureExactDecimals(value: string, decimals: number): string {
	if (!value) return "0,00";

	const parts = value.split(",");
	const integerPart = parts[0] ?? "0";
	const decimalPart = parts[1] ?? "";

	const paddedDecimals = decimalPart.padEnd(decimals, "0").slice(0, decimals);
	return `${integerPart},${paddedDecimals}`;
}

function formatCurrencyForEditing(
	rawValue: string,
	withThousands: boolean,
	decimals: number,
): string {
	let formatted = normalizeDecimalSeparator(rawValue);
	formatted = formatted.replace(/[^\d,]/g, "");

	const commaParts = formatted.split(",");
	if (commaParts.length > 2) {
		formatted = `${commaParts[0]},${commaParts.slice(1).join("")}`;
	}

	formatted = limitToExactDecimals(formatted, decimals);

	if (withThousands) {
		formatted = formatWithThousandsSeparators(formatted);
	}

	return formatted;
}

function formatCurrencyForDisplay(
	value: string,
	withThousands: boolean,
	isFocused: boolean,
	decimals: number,
): string {
	let formatted = value;
	if (!isFocused) {
		formatted = removeLeadingZeros(value);
		formatted = ensureExactDecimals(formatted, decimals);
	}

	if (withThousands) {
		formatted = formatWithThousandsSeparators(formatted);
	}

	return formatted;
}

function formatCurrencyFromNumber(
	numValue: number,
	withThousands: boolean,
	isFocused: boolean,
	decimals: number,
): string {
	if (Number.isNaN(numValue)) return "";
	const strValue = numValue.toString().replace(".", ",");
	return formatCurrencyForDisplay(strValue, withThousands, isFocused, decimals);
}

export const PriceInput = ({
	value,
	onChange,
	placeholder,
	disabled,
	label,
	error,
	formatThousands = true,
	className,
	required,
	iconOrText,
	iconOrTextPosition = "right",
	onBlur,
	onFocus,
	decimals = 2,
	...restProps
}: PriceInputProps) => {
	const [displayValue, setDisplayValue] = React.useState<string | undefined>(
		undefined,
	);
	const inputRef = React.useRef<HTMLInputElement>(null);
	const cursorPositionRef = React.useRef<number | null>(null);
	const isFocusedRef = React.useRef(false);
	const previousValueRef = React.useRef<number | undefined>(undefined);

	React.useLayoutEffect(() => {
		if (cursorPositionRef.current !== null && inputRef.current) {
			inputRef.current.setSelectionRange(
				cursorPositionRef.current,
				cursorPositionRef.current,
			);
			cursorPositionRef.current = null;
		}
	});

	React.useEffect(() => {
		if (!value) return;
		if (Object.is(value, previousValueRef.current)) {
			return;
		}

		previousValueRef.current = value;
		const newDisplay = formatCurrencyFromNumber(
			value,
			formatThousands,
			isFocusedRef.current,
			decimals,
		);
		setDisplayValue(newDisplay);
	}, [value, formatThousands, decimals]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;
		const cursorPosition = inputRef.current?.selectionStart ?? 0;
		const formatted = formatCurrencyForEditing(
			inputValue,
			formatThousands,
			decimals,
		);

		const lengthDifference = formatted.length - inputValue.length;
		const newCursorPosition = Math.min(
			cursorPosition + lengthDifference,
			formatted.length,
		);
		cursorPositionRef.current = newCursorPosition;

		setDisplayValue(formatted);
		const numValue = parseFrenchNumber(formatted);
		onChange(numValue.toString());
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		isFocusedRef.current = false;

		if (displayValue) {
			const formatted = formatCurrencyForDisplay(
				displayValue,
				formatThousands,
				false,
				decimals,
			);
			setDisplayValue(formatted);
		}

		if (onBlur) {
			onBlur(e);
		}
	};

	const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		isFocusedRef.current = true;
		if (onFocus) {
			onFocus(e);
		}
	};

	return (
		<div className="*:not-first:mt-2 w-full flex flex-col items-start">
			{label && (
				<p>
					{label} {required && <span className="text-destructive">*</span>}
				</p>
			)}
			<div className="relative w-full">
				<input
					ref={inputRef}
					data-slot="input"
					className={cn(
						"border-input placeholder:text-muted-foreground/70 flex h-11 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
						"focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
						"[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
						iconOrTextPosition === "left" && "ps-9",
						iconOrTextPosition === "right" && "pe-9",
						className,
					)}
					aria-invalid={!!error}
					type="text"
					inputMode="decimal"
					placeholder={placeholder ?? ""}
					value={displayValue ?? ""}
					onChange={handleChange}
					onBlur={handleBlur}
					onFocus={handleFocus}
					disabled={disabled}
					{...restProps}
				/>
				{iconOrText && (
					<div
						className={cn(
							"text-muted-foreground/80 pointer-events-none absolute inset-y-0 flex items-center justify-center peer-disabled:opacity-50",
							iconOrTextPosition === "left" && "left-0 ps-3",
							iconOrTextPosition === "right" && "right-0 pe-3",
						)}
					>
						{iconOrText}
					</div>
				)}
			</div>
			{error && (
				<p
					className="text-destructive mt-2 text-xs"
					role="alert"
					aria-live="polite"
				>
					{error}
				</p>
			)}
		</div>
	);
};

export const parsePriceToNumber = (
	s: string | undefined | null,
): number | null | undefined => {
	if (!s) return null;
	const normalized = s.replace(/\s/g, "").replace(/,/g, ".");
	const num = Number(normalized);
	return Number.isNaN(num) ? undefined : num;
};
